import Spinner from "presentation/components/Shared/Spinner";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useFilterUserStore } from "presentation/stores/use-filter-user-store";
import { useUserByFilter } from "presentation/hooks/use-query-user";
import { UserInputFilter } from "domain/models/user-input-filter";
import HeaderUserGrid from "./HeaderUserGrid";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";

const UserGrid: React.FC = () => {
  const { filters, setFilters } = useFilterUserStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "email",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useUserByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as UserInputFilter);

  const { data: employees } = useEmployeesByFilter({
    limit: 100,
  } as EmployeeInputFilter);

  const usersWithNames = users?.data?.map((user, index) => {
    return {
      ...users?.data?.[index],
      name: employees?.data?.find((emp) => emp.emailAddress === user.email)
        ?.name,
    };
  });

  useEffect(() => {
    refetch();
  }, [lazyParams, refetch]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  return (
    <div>
      <DataTable
        value={usersWithNames || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={users?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderUserGrid />}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["name", "roles"]}
      >
        <Column
          field="email"
          header="Utilizador"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="name"
          header="Nome"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="roles"
          header="Funções"
          filter
          filterField="query"
          showFilterMatchModes={false}
          body={(rowData) =>
            rowData.roles?.map((role: any) => role.name).join(", ")
          }
        />
      </DataTable>
    </div>
  );
};

export default UserGrid;
